import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import ar from './locales/ar.json';
import ckb from './locales/ckb.json';
import en from './locales/en.json';

/**
 * Per-area translation files, merged over the core bundle above.
 *
 * Each feature owns `locales/<lang>/<area>.json` and adds its own top-level
 * namespace there. Without this, every feature would be appending to the same
 * three JSON files, which turns each new screen into a merge conflict.
 *
 * Vite resolves the glob at build time, so nothing is fetched at runtime.
 */
const areaBundles = import.meta.glob<Record<string, unknown>>('./locales/*/*.json', {
  eager: true,
  import: 'default',
});

function mergeAreaBundles(language: string, core: Record<string, unknown>) {
  const merged: Record<string, unknown> = { ...core };
  for (const [path, bundle] of Object.entries(areaBundles)) {
    // './locales/en/cafe.json' -> 'en'
    if (path.split('/')[2] === language) Object.assign(merged, bundle);
  }
  return merged;
}

/**
 * Supported staff-app languages.
 *
 * Kurdish (Sorani) uses the `ckb` ISO 639-3 code, not `ku` — `ku` is Kurmanji,
 * which is a different language written in Latin script and is NOT what staff
 * in Sulaymaniyah read.
 */
export const LANGUAGES = {
  en: { label: 'English', nativeLabel: 'English', dir: 'ltr' },
  ar: { label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl' },
  ckb: { label: 'Kurdish', nativeLabel: 'کوردی', dir: 'rtl' },
} as const;

export type LanguageCode = keyof typeof LANGUAGES;

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

const STORAGE_KEY = 'gc.language';

export function isLanguageCode(value: unknown): value is LanguageCode {
  return typeof value === 'string' && value in LANGUAGES;
}

function readStoredLanguage(): LanguageCode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguageCode(stored)) return stored;
  } catch {
    // localStorage can throw in locked-down kiosk contexts; fall through.
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Applies language + direction to the document root.
 *
 * Both `lang` and `dir` matter: `dir` flips the layout (all components use
 * logical properties), while `lang` selects the Arabic-script font stack and
 * drives correct locale-aware number and date formatting.
 */
export function applyDocumentLanguage(language: LanguageCode): void {
  const root = document.documentElement;
  root.setAttribute('lang', language);
  root.setAttribute('dir', LANGUAGES[language].dir);
}

export async function changeLanguage(language: LanguageCode): Promise<void> {
  await i18n.changeLanguage(language);
  applyDocumentLanguage(language);
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Non-fatal: the language still applies for this session.
  }
}

const initialLanguage = readStoredLanguage();

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: mergeAreaBundles('en', en) },
    ar: { translation: mergeAreaBundles('ar', ar) },
    ckb: { translation: mergeAreaBundles('ckb', ckb) },
  },
  lng: initialLanguage,
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    // React already escapes rendered values.
    escapeValue: false,
  },
  returnNull: false,
});

applyDocumentLanguage(initialLanguage);

export default i18n;
